import { createContext, useCallback, useMemo, useState } from "react";
import { UserClient } from "../lib/usersBEClient";

type ICompanyContext = {
   company: {
      id: string;
      name: string;
      subName: string;
      color: string;
      logo: File;
      intern: ("denuncia" | "queja" | "sugerencia")[];
      wt: string;
      branches: any[];
      group: string;
      suggestions: string[];
   };
   getInfo: (subName: string) => void;
   notFound: boolean;
   userClient: UserClient;
   s3Bucket: {
      isGroup: boolean;
      bucket: string;
   };
};

export const CompanyContext = createContext<ICompanyContext>({
   company: {
      id: "",
      name: "",
      subName: "",
      color: "",
      intern: [],
      logo: undefined,
      wt: "",
      branches: [],
      group: null,
      suggestions: [],
   },
   getInfo: () => {},
   notFound: false,
   userClient: new UserClient(),
   s3Bucket: null,
});

export const CompanyProvider = ({ children }) => {
   const [notFound, setNotFound] = useState(false);
   const [company, setCompany] = useState({
      id: "",
      name: "",
      color: "",
      subName: "",
      logo: undefined,
      intern: [],
      wt: "",
      group: null,
      branches: [],
      suggestions: [],
   });
   const [s3Bucket, setS3Bucket] = useState(null);
   const userClient = useMemo(() => new UserClient(), []);

   const getCompanyLogo = useCallback(
      async (clientId: string) => {
         try {
            const logoUrl: string = await userClient.getUrlForS3File(clientId, "logo.png");
            const companyLogo = await fetch(logoUrl).then(async (data) => {
               const newBlob = await data.blob();
               const file = new File([newBlob], "logo.png", { type: "image/png" });
               return file;
            });
            return companyLogo;
         } catch (error) {}
      },
      [userClient]
   );

   const getInfo = useCallback(
      async (subName: string) => {
         try {
            const PlatformInfo = await userClient.getPlatformInfo(subName);
            //if (PlatformInfo.disabled) throw new AxiosError("disabled");
            const branchesInfo = await userClient.getCompanyBranches(PlatformInfo.companyInfo._id);

            const branchesName: any[] = [];
            for (const branch of branchesInfo) {
               branchesName.push({ name: branch.name, id: branch._id });
            }
            const companyLogo = await getCompanyLogo(PlatformInfo.companyInfo._id);
            setCompany({
               ...company,
               id: PlatformInfo.companyInfo._id,
               group: PlatformInfo.companyInfo.group,
               color: PlatformInfo.companyInfo.company_details.primaryColor || PlatformInfo.platformInfo.color,
               wt: PlatformInfo.platformInfo.message,
               intern: PlatformInfo.platformInfo.intern,
               name: PlatformInfo.companyInfo.person_details.comercialName,
               subName: PlatformInfo.platformInfo.subName,
               logo: companyLogo,
               branches: branchesName,
               suggestions: PlatformInfo.platformInfo.suggestions || [],
            });
            setS3Bucket({
               isGroup: !!PlatformInfo.companyInfo.group,
               bucket: PlatformInfo.companyInfo.group || PlatformInfo.companyInfo._id,
            });
         } catch (err) {
            setNotFound(true);
         }
      },
      [company, getCompanyLogo, userClient]
   );

   const values = useMemo(() => {
      return {
         company,
         getInfo,
         notFound,
         userClient,
         s3Bucket,
      };
   }, [company, getInfo, notFound, userClient]);

   return <CompanyContext.Provider value={values}>{children}</CompanyContext.Provider>;
};
